import { type ComponentType } from 'react';

import {
  DeveloperGraphic,
  EnterpriseGraphic,
  InvisibleChainGraphic,
  IssuanceGraphic,
  PaymentsGraphic,
  PoliciesGraphic,
  TreasuryGraphic,
} from '@/components/sections/Capabilities';
import {
  WarpedTexture,
  type WarpedTexturePreset,
} from '@/components/three/SecurityCardTexture';
import { Container } from '@/components/ui/container';
import { Reveal } from '@/components/ui/reveal';
import { Section, SectionHeading } from '@/components/ui/section';
import { capabilityDetails } from '@/content/site';
import { cn } from '@/lib/utils';

type CapabilityId = (typeof capabilityDetails.items)[number]['id'];
type TextureOverlay = 'security' | 'assistant' | 'proactive';
type VisualWidth = 'wide' | 'medium' | 'compact';

type VisualConfig = {
  graphic: ComponentType;
  overlay: TextureOverlay;
  texture: WarpedTexturePreset;
  width: VisualWidth;
};

/**
 * One unique shader for every expanded capability: three from SecurityLayers
 * plus the four exact annotated reference treatments.
 */
const VISUALS = {
  treasury: {
    graphic: TreasuryGraphic,
    overlay: 'assistant',
    texture: 'assistantBlue',
    width: 'wide',
  },
  payments: {
    graphic: PaymentsGraphic,
    overlay: 'security',
    texture: 'active',
    width: 'compact',
  },
  issuance: {
    graphic: IssuanceGraphic,
    overlay: 'assistant',
    texture: 'assistantGreen',
    width: 'compact',
  },
  products: {
    graphic: DeveloperGraphic,
    overlay: 'security',
    texture: 'verified',
    width: 'wide',
  },
  chain: {
    graphic: InvisibleChainGraphic,
    overlay: 'proactive',
    texture: 'proactiveSlate',
    width: 'compact',
  },
  policies: {
    graphic: PoliciesGraphic,
    overlay: 'assistant',
    texture: 'assistantDark',
    width: 'compact',
  },
  enterprise: {
    graphic: EnterpriseGraphic,
    overlay: 'security',
    texture: 'waiting',
    width: 'medium',
  },
} satisfies Record<CapabilityId, VisualConfig>;

const VISUAL_GRID_CLASSES = {
  wide: {
    standard: 'min-[900px]:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]',
    reverse: 'min-[900px]:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]',
  },
  medium: {
    standard: 'min-[900px]:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]',
    reverse: 'min-[900px]:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]',
  },
  compact: {
    standard: 'min-[900px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]',
    reverse: 'min-[900px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]',
  },
} satisfies Record<VisualWidth, Record<'standard' | 'reverse', string>>;

function CapabilityVisual({
  capability,
  side,
  visual,
}: {
  capability: string;
  side: 'left' | 'right';
  visual: VisualConfig;
}) {
  const Graphic = visual.graphic;

  return (
    <div
      aria-label={`${capability} product preview`}
      role="img"
      className={cn(
        'capability-detail-stage relative isolate min-h-[20rem] overflow-hidden rounded-b-2xl border-t border-black/5 sm:min-h-[24rem] min-[900px]:min-h-[41rem]',
        side === 'left'
          ? 'min-[900px]:rounded-bl-2xl min-[900px]:rounded-br-none min-[900px]:rounded-tl-2xl min-[900px]:rounded-tr-none min-[900px]:border-r min-[900px]:border-t-0'
          : 'min-[900px]:rounded-bl-none min-[900px]:rounded-br-2xl min-[900px]:rounded-tl-none min-[900px]:rounded-tr-2xl min-[900px]:border-l min-[900px]:border-t-0'
      )}
    >
      <div
        aria-hidden
        data-overlay={visual.overlay}
        className="capability-detail-texture pointer-events-none absolute inset-0 z-0 overflow-hidden"
      >
        <WarpedTexture preset={visual.texture} className="opacity-95" />
      </div>

      <div
        aria-hidden
        className="capability-detail-demo pointer-events-none absolute left-1/2 top-1/2 z-10 w-[min(45rem,calc(100%-1.25rem))] -translate-x-1/2 -translate-y-1/2 sm:w-[min(45rem,calc(100%-3rem))]"
      >
        <Graphic />
      </div>
    </div>
  );
}

export function CapabilityDetails() {
  return (
    <Section
      id="capability-details"
      className="overflow-hidden border-t border-hairline bg-white"
    >
      <Container>
        <SectionHeading
          eyebrow={capabilityDetails.eyebrow}
          title={capabilityDetails.title}
          description={capabilityDetails.description}
          align="left"
          className="items-center text-center sm:items-start sm:text-left"
          descriptionClassName="max-w-full text-balance"
        />

        <div className="mt-12 space-y-12 sm:mt-16 sm:space-y-16 lg:space-y-20">
          {capabilityDetails.items.map((item, index) => {
            const visual = VISUALS[item.id];
            const reverse = index % 2 === 0;
            const headingId = `capability-detail-${item.id}`;

            return (
              <section
                key={item.id}
                id={`capability-${item.id}`}
                aria-labelledby={headingId}
                data-testid={`capability-detail-${item.id}`}
                className="rounded-2xl border border-hairline/50 bg-zinc-50/90"
              >
                <div
                  className={cn(
                    'grid gap-4 min-[900px]:items-center min-[900px]:gap-5',
                    VISUAL_GRID_CLASSES[visual.width][
                      reverse ? 'reverse' : 'standard'
                    ]
                  )}
                >
                  <Reveal
                    className={cn(
                      'flex min-w-0 items-center px-5 py-8 sm:px-6 sm:py-10 min-[900px]:py-0',
                      reverse ? 'min-[900px]:order-2' : 'min-[900px]:pl-11'
                    )}
                  >
                    <div className="w-full max-w-[32rem]">
                      <p className="text-[15px] font-normal leading-[1.5] tracking-[0.005em] text-slate-900/60">
                        {item.capability}
                      </p>
                      <h3
                        id={headingId}
                        className="mt-2 text-balance text-2xl font-medium leading-[1.25] tracking-normal text-slate-900"
                      >
                        {item.title}
                      </h3>

                      <ul className="mt-4 grid gap-2">
                        {item.points.map((point) => (
                          <li
                            key={point.title}
                            className="grid grid-cols-[0.4rem_minmax(0,1fr)] gap-2"
                          >
                            <span className="mt-[0.54rem] size-[5px] rounded-full bg-slate-900/50" />
                            <span>
                              <strong className="block text-[15px] font-medium leading-[1.5] tracking-[0.005em] text-slate-900">
                                {point.title}
                              </strong>
                              <span className="block text-[15px] font-normal leading-[1.5] tracking-[0.005em] text-slate-900/60">
                                {point.description}
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>

                  <Reveal
                    delay={80}
                    className={cn('min-w-0', reverse && 'min-[900px]:order-1')}
                  >
                    <CapabilityVisual
                      capability={item.capability}
                      side={reverse ? 'left' : 'right'}
                      visual={visual}
                    />
                  </Reveal>
                </div>
              </section>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
